import React from "react"
import pluralize from "pluralize"
import {
    Box,
    componentStyles,
    Image,
    useTheme,
    VerticalStack,
    ExternalLink,
    HeadingMedium,
} from "@mallardbay/lib-react-components"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faFacebookF } from "@fortawesome/free-brands-svg-icons/faFacebookF"
import { faInstagram } from "@fortawesome/free-brands-svg-icons/faInstagram"
import { faTwitter } from "@fortawesome/free-brands-svg-icons/faTwitter"

import type { UserSocialsFieldsFragment } from "~graphql/generated/graphql"

import Span from "./todo-lib-react-components/span"

export default function ProfileCard({
    avatar,
    name,
    position,
    experience,
    bio,
    socials,
}: Props) {
    const styles = useStyles()

    return (
        <Box style={styles.root}>
            <Box style={styles.heading}>
                <Image src={avatar} style={styles.avatar} />
                <VerticalStack style={styles.nameAndExperienceContainer}>
                    <HeadingMedium style={styles.name}>{name}</HeadingMedium>
                    <Box style={styles.experience}>
                        {position && (
                            <Span style={styles.position}>{position}</Span>
                        )}
                        {!!experience &&
                            ` · ${pluralize(
                                "year",
                                experience,
                                true
                            )} of experience`}
                    </Box>
                </VerticalStack>
            </Box>
            <Box style={styles.bio}>{bio}</Box>
            {socials && (
                <Box style={styles.socials}>
                    {socials.facebook && (
                        <ExternalLink href={socials.facebook}>
                            <FontAwesomeIcon
                                style={styles.socialIcon}
                                icon={faFacebookF}
                            />
                        </ExternalLink>
                    )}
                    {socials.twitter && (
                        <ExternalLink href={socials.twitter}>
                            <FontAwesomeIcon
                                style={styles.socialIcon}
                                icon={faTwitter}
                            />
                        </ExternalLink>
                    )}
                    {socials.instagram && (
                        <ExternalLink href={socials.instagram}>
                            <FontAwesomeIcon
                                style={styles.socialIcon}
                                icon={faInstagram}
                            />
                        </ExternalLink>
                    )}
                </Box>
            )}
        </Box>
    )
}

interface Props {
    readonly avatar: string
    readonly name: string
    readonly position: string
    readonly experience?: number | null
    readonly bio?: string
    readonly socials?: UserSocialsFieldsFragment | null
}

function useStyles() {
    const { colors, shadows } = useTheme()

    return componentStyles({
        root: {
            minWidth: "280px",
            backgroundColor: colors.bgColor,
            padding: { base: "24px", lg: "32px" },
            borderRadius: "16px",
            borderWidth: "1px",
            borderStyle: "solid",
            borderColor: colors.borderOpaque,
            boxShadow: shadows.lg,
        },
        heading: {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
        },
        avatar: {
            height: "94px",
            width: "94px",
            objectFit: "cover",
            borderRadius: "50%",
            marginBottom: "20px",
        },
        nameAndExperienceContainer: {
            textAlign: "center",
        },
        name: {
            width: "100%",
        },
        experience: {
            textAlign: "center",
            fontSize: { base: "14px", sm: "16px" },
            fontWeight: 300,
            color: colors.contentSecondary,
        },
        position: {
            textAlign: "center",
            fontSize: { base: "14px", sm: "16px" },
            fontWeight: 600,
            color: colors.custom.brand,
        },
        bio: {
            marginTop: "24px",
            fontSize: "16px",
            fontWeight: 300,
            lineHeight: "22px",
        },
        socials: {
            width: "100%",
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
        },
        socialIcon: {
            fontSize: "18px",
            margin: "16px 8px 0 8px",
            color: colors.contentPrimary,
            ":hover": {
                cursor: "pointer",
                color: colors.contentSecondary,
            },
        },
    })
}
